import { type TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL } from '@sorare/core/src/atoms/typography';
import { RevealPackCards } from '@sorare/core/src/components/cards/RevealPackCards';
import Dialog from '@sorare/core/src/components/dialog';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import {
  mediumMobileHeightAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import type {
  StarterPackOpeningMutation,
  StarterPackOpeningMutationVariables,
} from './__generated__/index.graphql';

const StyledDialog = styled(Dialog)`
  background: linear-gradient(to bottom, #2c2b70 0%, rgba(44, 43, 112, 0) 100%);
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150-opaque);
`;
const Wrapper = styled(Vertical).attrs({ center: true })`
  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    min-width: 400px;
  }
`;
const CardPackPictureWrapper = styled.button`
  position: relative;
  isolation: isolate;
  & > img {
    width: 250px;
  }
  height: 360px;
  @media ${mediumMobileHeightAndAbove} {
    height: 500px;
  }
`;
const PackRevealWrapper = styled.div`
  max-width: 400px;
  padding: 0 var(--quadruple-unit);
`;

const CLAIM_FIRST_PACK_MUTATION = gql`
  mutation StarterPackOpeningMutation($input: claimStarterPackInput!) {
    claimStarterPack(input: $input) {
      cards {
        slug
        pictureUrl
      }
      currentUser {
        slug
        footballUserProfile {
          id
          commonGameOnboarded
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  StarterPackOpeningMutation,
  StarterPackOpeningMutationVariables
>;

type Props = {
  cardPackPictureUrl: Nullable<string>;
  onContinue: () => void;
};
const StarterPackOpeningDialogContent = ({
  cardPackPictureUrl,
  onContinue,
}: Props) => {
  const [claimPack, { loading }] = useMutation(CLAIM_FIRST_PACK_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const [cardsPictures, setCardsPictures] = useState<string[]>([]);

  const onClaimPack = async () => {
    const { data } = await claimPack({
      variables: {
        input: {
          sport: Sport.FOOTBALL,
          favoriteTeamSlug: 'bayern-munchen-munchen',
          starPlayerSlug: 'jamal-musiala',
        },
      },
    });

    setCardsPictures(
      data?.claimStarterPack?.cards?.map(c => c.pictureUrl).filter(Boolean) ||
        []
    );
  };

  if (cardsPictures.length) {
    return (
      <Wrapper>
        <PackRevealWrapper>
          <RevealPackCards cards={cardsPictures} onContinue={onContinue} />
        </PackRevealWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper center>
      <CardPackPictureWrapper onClick={onClaimPack} disabled={loading}>
        {cardPackPictureUrl && (
          <img src={cardPackPictureUrl} alt="" width={160} />
        )}
      </CardPackPictureWrapper>
    </Wrapper>
  );
};

export const StarterPackOpeningDialog = ({
  open,
  onClose,
  ...otherProps
}: Props & {
  open: boolean;
  onClose: () => void;
}) => {
  return (
    <StyledDialog
      open={open}
      maxWidth="sm"
      onClose={onClose}
      title={
        <HeadlineL>
          <FormattedMessage
            id="OpenStarterPackPage.title"
            defaultMessage="Open your first pack"
          />
        </HeadlineL>
      }
    >
      <StarterPackOpeningDialogContent {...otherProps} />
    </StyledDialog>
  );
};
